// The pieces Secret Sample's pages are built from.
//
//   VinylReveal   the seed you were handed, as a record in a sleeve: sealed
//                 (a "?") until the draw, then it slides out and spins
//   EntryTicket   "You're in", with the loop you put in the pool on its stub
//   PoolPicker    one of my seeds into the pool, or upload a new one
//   JoinFlow      after Join: add a loop or just play, then the Discord call
//   DateBlock     a calendar tile: month, day, time
//   StatusBadge   Upcoming / Live now / Ended

import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, ExternalLink, Music2, Upload, X } from "lucide-react";
import type { ApiEventPoolSeed, ApiEventView, ApiPoolableSeed, EventStatus } from "@shared/api-types.js";
import { Button } from "@web-shared/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@web-shared/components/ui/dialog";
import { Skeleton } from "@web-shared/components/ui/skeleton";
import { json, webApi } from "@web-shared/lib/web-api";
import { cn } from "@web-shared/lib/utils";
import { eventUploadPath, localRoomPath, profilePath, seedPath } from "@/lib/ctas";

// ---- Vinyl ---------------------------------------------------------------------

/**
 * The record and its sleeve. Sealed, the sleeve sits centred with a "?" on
 * it; revealed, it slides left and the record slides out to the right and
 * spins. The reveal plays every time the page opens on an assignment — a
 * beat after mount, so the move is seen rather than arrived at.
 */
export function VinylReveal({ seed, eventTitle }: { seed: ApiEventPoolSeed | null; eventTitle: string }) {
  const [out, setOut] = useState(false);
  useEffect(() => {
    if (!seed) {
      setOut(false);
      return;
    }
    const t = setTimeout(() => setOut(true), 350);
    return () => clearTimeout(t);
  }, [seed]);

  return (
    <div className="relative mx-auto h-[190px] w-[340px] max-w-full sm:h-[240px] sm:w-[420px]" aria-hidden>
      {/* The record: behind the sleeve, then out of it. */}
      <div
        className="absolute top-[4%] aspect-square h-[92%] transition-[left] duration-[1100ms] ease-[cubic-bezier(0.2,0.8,0.2,1)]"
        style={{ left: out ? "46%" : "27%" }}
      >
        <div className={cn("relative size-full rounded-full shadow-[0_10px_40px_-10px_rgba(0,0,0,0.8)]", out && "record-spin")}>
          <div
            className="absolute inset-0 rounded-full"
            style={{ background: "repeating-radial-gradient(circle at center, #121215 0 1.5px, #24242a 1.5px 3px)", boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.08)" }}
          />
          {/* The label: the room's green, a centre hole, a mark that shows it turning. */}
          <div className="absolute inset-[33%] rounded-full bg-primary">
            <div className="absolute left-1/2 top-[14%] h-[18%] w-[6%] -translate-x-1/2 rounded-full bg-primary-foreground/40" />
            <div className="absolute inset-[42%] rounded-full bg-[#0c0c0e]" />
          </div>
        </div>
        {/* Light on the grooves stays put while the record turns. */}
        <div
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            background:
              "conic-gradient(from 20deg, transparent 0 8%, rgba(255,255,255,0.09) 14%, transparent 22% 55%, rgba(255,255,255,0.06) 62%, transparent 70%)",
          }}
        />
      </div>

      {/* The sleeve. */}
      <div
        className="absolute top-0 aspect-square h-full overflow-hidden rounded-lg border border-white/10 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.9)] transition-[left] duration-[1100ms] ease-[cubic-bezier(0.2,0.8,0.2,1)]"
        style={{
          left: out ? "0%" : "22%",
          background:
            "radial-gradient(120% 90% at 0% 0%, color-mix(in srgb, var(--primary) 55%, transparent), transparent 60%), radial-gradient(90% 90% at 100% 100%, rgba(28,57,142,0.75), transparent 65%), #101013",
        }}
      >
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #fff 0 1px, transparent 1px 9px)" }} />
        <div className="relative flex h-full flex-col justify-between p-4">
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-white/80">Secret Sample</span>
          {seed ? (
            <span className="line-clamp-2 text-left text-sm font-bold leading-tight text-white/90">{eventTitle}</span>
          ) : (
            <span className="text-7xl font-black leading-none text-white/90 sm:text-8xl">?</span>
          )}
        </div>
      </div>
    </div>
  );
}

/** Under the record: what it is, and where to take it. */
export function AssignmentDetails({ seed }: { seed: ApiEventPoolSeed }) {
  return (
    <div className="space-y-4 text-center">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-primary">Your Secret Sample</p>
        <p className="mt-1 text-3xl font-black tracking-tight sm:text-4xl">{seed.title}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          by{" "}
          {seed.producer.handle ? (
            <Link to={profilePath(seed.producer.handle)} className="font-semibold text-foreground hover:underline">
              {seed.producer.username}
            </Link>
          ) : (
            <span className="font-semibold text-foreground">{seed.producer.username}</span>
          )}
        </p>
      </div>
      <div className="flex flex-col items-center justify-center gap-2 sm:flex-row">
        <Button asChild className="h-12 w-full rounded-full px-7 text-base font-bold sm:w-auto">
          <Link to={seedPath(seed.versionId)}>Open the seed</Link>
        </Button>
        <Button asChild variant="outline" className="hidden h-12 rounded-full px-6 font-semibold sm:inline-flex">
          <Link to={localRoomPath(seed.versionId)}>Flip it in the browser</Link>
        </Button>
      </div>
    </div>
  );
}

// ---- Ticket --------------------------------------------------------------------

/**
 * "You're in" as an entry ticket: the pass on top, a perforated tear line,
 * and the stub below holding what you put in the pool.
 */
export function EntryTicket({ title, sub, stub, aside }: { title: string; sub: ReactNode; stub: ReactNode; aside?: ReactNode }) {
  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-primary/25 bg-raised"
      style={{ backgroundImage: "linear-gradient(120deg, color-mix(in srgb, var(--primary) 9%, transparent), transparent 55%)" }}
    >
      <div className="flex items-start gap-4 p-5 sm:p-6">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary ring-1 ring-primary/30">
          <Check className="size-5" strokeWidth={3} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-2xl font-black tracking-tight">{title}</p>
          <div className="mt-1 text-sm text-muted-foreground">{sub}</div>
        </div>
        {aside}
      </div>
      {/* The tear line, with a notch bitten out of each edge. */}
      <div className="relative">
        <div className="mx-5 border-t-2 border-dashed border-white/10" />
        <div className="absolute -left-3 top-1/2 size-6 -translate-y-1/2 rounded-full border border-primary/25 bg-background" />
        <div className="absolute -right-3 top-1/2 size-6 -translate-y-1/2 rounded-full border border-primary/25 bg-background" />
      </div>
      <div className="p-5 sm:p-6">{stub}</div>
    </section>
  );
}

// ---- Pool ----------------------------------------------------------------------

export function usePoolable(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: ["web", "event", slug, "poolable"],
    queryFn: () => webApi<{ seeds: ApiPoolableSeed[] }>(`/events/${encodeURIComponent(slug)}/poolable`),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: "always",
  });
}

/** My seeds, one tap into the pool. `onPooled` after the server says yes. */
export function PoolPicker({
  slug,
  pooled,
  onPooled,
  midJoin = false,
}: {
  slug: string;
  pooled: ApiEventPoolSeed[];
  onPooled: () => void;
  /** Inside JoinFlow: an upload comes back to its next step. */
  midJoin?: boolean;
}) {
  const queryClient = useQueryClient();
  const mine = usePoolable(slug, true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = (versionId: string) => {
    setBusy(versionId);
    setError(null);
    webApi(`/events/${encodeURIComponent(slug)}/pool`, json("POST", { versionId }))
      .then(async () => {
        await queryClient.invalidateQueries({ queryKey: ["web", "event", slug] });
        onPooled();
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Couldn't add it."))
      .finally(() => setBusy(null));
  };

  return (
    <div className="space-y-2">
      {mine.isPending ? (
        <Skeleton className="h-28 w-full rounded-xl" />
      ) : mine.isError ? (
        <p className="text-sm text-removed">Couldn't load your seeds.</p>
      ) : mine.data.seeds.length === 0 ? (
        <p className="rounded-xl border border-dashed border-white/15 p-4 text-center text-sm text-muted-foreground">
          No public seeds yet. Upload one below.
        </p>
      ) : (
        <ul className="max-h-64 space-y-1 overflow-y-auto pr-1">
          {mine.data.seeds.map((s) => {
            const already = pooled.some((p) => p.versionId === s.versionId);
            return (
              <li key={s.versionId}>
                <button
                  type="button"
                  disabled={busy !== null || already}
                  onClick={() => pick(s.versionId)}
                  className="group flex w-full items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 text-left transition-colors hover:border-primary/30 hover:bg-primary/[0.06] disabled:opacity-60"
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-white/5 text-muted-foreground group-hover:text-primary">
                    <Music2 className="size-4" />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold">{s.title}</span>
                  {s.visibility === "unlisted" && <span className="text-[11px] text-muted-foreground">Unlisted</span>}
                  {already ? (
                    <span className="text-[11px] font-bold uppercase tracking-widest text-primary">In</span>
                  ) : busy === s.versionId ? (
                    <span className="text-xs text-muted-foreground">Adding…</span>
                  ) : (
                    <ArrowRight className="size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <Link
        to={eventUploadPath(slug, midJoin ? "discord" : undefined)}
        className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 px-3 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
      >
        <Upload className="size-4" /> Upload a new seed
      </Link>
      {error && <p className="text-sm text-removed">{error}</p>}
    </div>
  );
}

/** A seed in the pool, with ✕ while it can still come out. */
export function PooledRow({ seed, onRemove, busy }: { seed: ApiEventPoolSeed; onRemove?: () => void; busy?: boolean }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-white/[0.04] px-3 py-2.5 ring-1 ring-white/10">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary">
        <Music2 className="size-4" />
      </span>
      <Link to={seedPath(seed.versionId)} className="min-w-0 flex-1 truncate text-sm font-semibold hover:underline">
        {seed.title}
      </Link>
      <span className="text-[10px] font-bold uppercase tracking-widest text-primary">In the pool</span>
      {onRemove && (
        <button
          type="button"
          disabled={busy}
          onClick={onRemove}
          aria-label={`Take ${seed.title} out of the pool`}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );
}

// ---- Join flow -----------------------------------------------------------------

export type JoinStep = "pool" | "discord";

/**
 * What Join walks you through once you have an account and are in: a loop
 * for the pool (or not), then the call. Closing it at any point leaves you
 * joined; the page has everything the steps did.
 */
export function JoinFlow({
  slug,
  view,
  step,
  onStep,
  onClose,
}: {
  slug: string;
  view: ApiEventView;
  step: JoinStep | null;
  onStep: (step: JoinStep) => void;
  onClose: () => void;
}) {
  const discord = view.event.discordUrl;
  const steps: JoinStep[] = discord ? ["pool", "discord"] : ["pool"];
  const next = () => {
    if (step === "pool" && discord) onStep("discord");
    else onClose();
  };
  return (
    <Dialog open={step !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md overflow-hidden rounded-2xl p-0">
        <div
          className="space-y-5 p-6"
          style={{ backgroundImage: "radial-gradient(90% 60% at 0% 0%, color-mix(in srgb, var(--primary) 12%, transparent), transparent)" }}
        >
          <div className="flex items-center gap-1.5">
            {steps.map((s) => (
              <span key={s} className={cn("h-1 rounded-full transition-all", s === step ? "w-6 bg-primary" : "w-3 bg-white/15")} />
            ))}
          </div>

          {step === "pool" && (
            <>
              <div>
                <DialogTitle className="text-2xl font-black tracking-tight">You're in. Add a loop?</DialogTitle>
                <DialogDescription className="mt-1 text-sm text-muted-foreground">
                  Put one of your seeds in the pool and someone else flips it. You'll never get your own. Optional.
                </DialogDescription>
              </div>
              <PoolPicker slug={slug} pooled={view.me?.pooled ?? []} onPooled={next} midJoin={discord !== null} />
              <button type="button" onClick={next} className="w-full text-center text-sm font-semibold text-muted-foreground hover:text-foreground">
                Skip, I'll just play
              </button>
            </>
          )}

          {step === "discord" && discord && (
            <>
              <div>
                <DialogTitle className="text-2xl font-black tracking-tight">Join the call</DialogTitle>
                <DialogDescription className="mt-1 text-sm text-muted-foreground">
                  The draw, the flips and the playback all happen live on Discord.
                </DialogDescription>
              </div>
              <Button
                asChild
                className="h-12 w-full rounded-full bg-[#5865F2] text-base font-bold text-white hover:bg-[#4752c4]"
              >
                <a href={discord} target="_blank" rel="noopener" onClick={onClose}>
                  Open Discord <ExternalLink className="size-4" />
                </a>
              </Button>
              <button type="button" onClick={onClose} className="w-full text-center text-sm font-semibold text-muted-foreground hover:text-foreground">
                I'm already there
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---- Dates and status ----------------------------------------------------------

/** A calendar tile: "OCT / 10 / 8:00 PM". */
export function DateBlock({ iso, className }: { iso: string | null; className?: string }) {
  const d = iso ? new Date(iso) : null;
  const ok = d !== null && !Number.isNaN(d.getTime());
  return (
    <div
      className={cn(
        "flex w-16 shrink-0 flex-col items-center overflow-hidden rounded-xl border border-white/10 bg-background/60 text-center",
        className,
      )}
    >
      <span className="w-full bg-primary py-0.5 text-[10px] font-black uppercase tracking-widest text-primary-foreground">
        {ok ? d.toLocaleDateString(undefined, { month: "short" }) : "TBA"}
      </span>
      <span className="pt-1 text-2xl font-black leading-none">{ok ? d.getDate() : "–"}</span>
      <span className="pb-1.5 pt-0.5 text-[10px] text-muted-foreground">
        {ok ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : ""}
      </span>
    </div>
  );
}

export function StatusBadge({ status }: { status: EventStatus }) {
  if (status === "assigned") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
        <span className="size-1.5 animate-pulse rounded-full bg-primary" /> Live now
      </span>
    );
  }
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest",
        status === "open" ? "bg-white/10 text-foreground" : "bg-white/5 text-muted-foreground",
      )}
    >
      {status === "open" ? "Upcoming" : "Ended"}
    </span>
  );
}

/** "Saturday, Oct 10 · 8:00 PM CDT", in the viewer's own time zone. */
export function whenOf(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZoneName: "short" });
  return `${day} · ${time}`;
}

/** A small record: the banner's and the list's. Spins when `spinning`. */
export function MiniRecord({ spinning, className }: { spinning: boolean; className?: string }) {
  return (
    <div className={cn("relative aspect-square shrink-0 rounded-full shadow-[0_6px_24px_-6px_rgba(0,0,0,0.8)]", className)} aria-hidden>
      <div className={cn("absolute inset-0 rounded-full", spinning && "record-spin")}>
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: "repeating-radial-gradient(circle at center, #121215 0 1.2px, #24242a 1.2px 2.4px)",
            boxShadow: "inset 0 0 0 1.5px rgba(255,255,255,0.08)",
          }}
        />
        <div className="absolute inset-[32%] rounded-full bg-primary">
          <div className="absolute left-1/2 top-[14%] h-[20%] w-[8%] -translate-x-1/2 rounded-full bg-primary-foreground/40" />
          <div className="absolute inset-[40%] rounded-full bg-[#0c0c0e]" />
        </div>
      </div>
      <div
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{ background: "conic-gradient(from 20deg, transparent 0 8%, rgba(255,255,255,0.1) 14%, transparent 22% 55%, rgba(255,255,255,0.07) 62%, transparent 70%)" }}
      />
    </div>
  );
}

/** "Tonight, 8:00 PM" / "Tomorrow, 8:00 PM" / "Fri, Oct 16 · 8:00 PM" / "Starting now". */
export function soonOf(iso: string | null, now = new Date()): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  if (d.getTime() <= now.getTime()) return "Starting now";
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(d) - day(now)) / 86_400_000);
  if (days === 0) return `${d.getHours() >= 17 ? "Tonight" : "Today"}, ${time}`;
  if (days === 1) return `Tomorrow, ${time}`;
  return `${d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · ${time}`;
}
